import { Component, inject } from '@angular/core';
import { AppConfig } from '@app/core/app.config';
import { ColDefContext } from '@app/shared/grid/column-builder';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

/**
 * Renderer for a DPE_LIST field (climateLabel/powerLabel), displaying its value as a single colored
 * badge, the same way CheckResultCellComponent displays each half of a DPE Check's combined 'result' Field.
 */
@Component({
  selector: 'dpe-label-cell-renderer',
  templateUrl: './dpe-label-cell-renderer.component.html',
  styleUrls: ['./dpe-label-cell-renderer.component.scss'],
  standalone: false
})
export class DpeLabelCellRendererComponent implements ICellRendererAngularComp {
  protected value: string;
  protected colorClass: string;
  protected readonly emptyValue = inject(AppConfig).EMPTY_FIELD_VALUE;
  protected readonly gridNullFlag = inject(AppConfig).GRID_NULL_FLAG;

  /**
   * Method used by Ag-Grid to initialise the cell with grid parameters.
   * @param params Cell renderer params, whose value is the DPE label's raw code (e.g. 'A'), and whose ColDef
   * context carries the FieldConfig used to tell a climateLabel column from a powerLabel one.
   */
  public agInit(params: ICellRendererParams<any, string>): void {
    this.value = this.getValueToDisplay(params);
    this.colorClass = this.getColorClass(params);
  }

  /**
   * Method used by Ag-Grid to tell the grid to refresh this cell.
   * @param params Cell renderer params.
   * @return Always true, since this renderer can update itself in place.
   */
  public refresh(params: ICellRendererParams<any, string>): boolean {
    this.value = this.getValueToDisplay(params);
    this.colorClass = this.getColorClass(params);
    return true;
  }

  /**
   * Retrieve the DPE label's raw value to render, or undefined if it is empty.
   * @param params Cell renderer params.
   * @return The DPE label's value, or undefined if empty.
   */
  private getValueToDisplay(params: ICellRendererParams<any, string>): string {
    return (!params.value || params.value === this.gridNullFlag) ? undefined : params.value;
  }

  /**
   * Build the CSS class colouring the badge, based on whether the column is for the climate or the power label.
   * @param params Cell renderer params.
   * @return The CSS class to apply, or undefined if the cell has no value to display.
   */
  private getColorClass(params: ICellRendererParams<any, string>): string {
    const value = this.getValueToDisplay(params);
    if (!value) return undefined;

    const fieldCode = (params.colDef.context as ColDefContext)?.fieldConfig?.fieldCode;
    const colorPrefix = fieldCode === 'powerLabel' ? 'power-color-' : 'climate-color-';
    return colorPrefix + value.toLowerCase();
  }
}
